import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, LockOutlined, PlusOutlined, SettingOutlined, SyncOutlined, UnlockOutlined } from '@ant-design/icons';
import LoginStatusIndicator from '@components/common/elements/LoginStatusIndicator';
import KitplaySettingModal from '@components/common/form/KitplaySettingModal';
import PickUpDisplaySettingModal from '@components/common/form/PickUpDisplaySettingModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE, ORDER_DISPLAY, STATUS_ACTIVE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { deviceStatus, expireKitplayOptions, orderDisplayLayouts } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { convertUtcToLocalTime } from '@utils';
import { setData } from '@utils/localStorage';
import { Button, Modal } from 'antd';
import { Table } from 'antd';

import useOrderDeviceFormModal from './OrderDeviceFormModal';

import styles from '../kitplayShopAccount/index.module.scss';

const message = defineMessages({
    objectName: 'ReadyBoard mapping',
    deleteDeviceConfirm: 'Are you sure you want to delete this device?',
    deleteDeviceSuccess: 'Delete device successfully',
    deleteOrderDisplayConfirm: 'Are you sure you want to delete this ReadyBoard?',
    deleteOrderDisplaySuccess: 'Delete ReadyBoard successfully',
});

const ListOrderDevicePage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const collapseId = params.get('collapseId');
    const [loadingSubItems, setLoadingSubItems] = useState({});
    const [fetchedAt, setFetchedAt] = useState(null);

    const [searchParams, setSearchParams] = React.useState(null);
    const notification = useNotification();
    const [childData, setChildData] = useState({});
    const statusValues = translate.formatKeys(deviceStatus, ['label']);
    const expireValues = translate.formatKeys(expireKitplayOptions, ['label']);
    const [expandedRowKeys, setExpandedRowKeys] = useState(() => {
        return collapseId ? [(collapseId)] : [];
    });

    const [editingRowId, setEditingRowId] = useState(null);
    const [editingChildRowId, setEditingChildRowId] = useState(null);
    const [editingSettingId, setEditingSettingId] = useState(null);

    const [openSettingModal, setOpenSettingModal] = useState(false);
    const [selectedSettingRecord, setSelectedSettingRecord] = useState(null);

    const handleOpenSetting = (record) => {
        setSelectedSettingRecord(record);
        setOpenSettingModal(true);
        setEditingSettingId(record.id);
    };

    const handleCloseSettingModal = () => {
        setOpenSettingModal(false);
        setSelectedSettingRecord(null);
        setEditingSettingId(null);
    };

    const { execute: fetchSubItems } = useFetch(apiConfig.orderDisplay.getList, {
        immediate: false,
        mappingData: (response) => {
            if (response.result === true) {
                return {
                    data: response.data.content,
                    total: response.data.totalElements,
                };
            } else {
                console.error('Error: Invalid response format', response);
            }
        },
    });

    const handleFetch = (id) => {
        const params = { parentId: id };
        setLoadingSubItems((prev) => ({
            ...prev,
            [id]: true,
        }));
        fetchSubItems({
            params,
            onCompleted: (response) => {
                if (response && response.data) {
                    if (response.data.content) {
                        const filteredData = response.data.content;
                        setChildData((prevData) => ({
                            ...prevData,
                            [id]: filteredData,
                        }));
                    } else {
                        setChildData((prevData) => ({
                            ...prevData,
                            [id]: [],
                        }));
                    }
                } else {
                    notification({
                        message: 'Error',
                        type: 'error',
                    });
                }
                setLoadingSubItems((prev) => ({
                    ...prev,
                    [id]: false,
                }));
            },
            onError: (error) => {
                const messageContent = 'Error';
                notification({
                    message: messageContent,
                    type: 'error',
                });
                setLoadingSubItems((prev) => ({
                    ...prev,
                    [id]: false,
                }));
            },
        });
    };

    useEffect(() => {
        if (collapseId) {
            handleFetch(collapseId);
            setExpandedRowKeys((prevKeys) => {
                if (!prevKeys.includes(collapseId)) {
                    return [...prevKeys, collapseId];
                }
                return prevKeys;
            });
        }
    }, [collapseId]);

    const { execute: executeDeleteOrder } = useFetch(apiConfig.orderDisplay.delete);
    const { execute: executeDeleteDevice } = useFetch(apiConfig.kitplayDevice.delete);
    const handleExpand = (expanded, record) => {
        if (expanded) {
            if (!childData[record.id]) {
                handleFetch(record.id);
            }
            setExpandedRowKeys((prevKeys) => [...prevKeys, record.id]);
        } else {
            setExpandedRowKeys((prevKeys) => prevKeys.filter((id) => id !== record.id));
        }
    };

    const { data, mixinFuncs, queryFilter, loading, pagination, changePagination } = useListBase({
        apiConfig: apiConfig.kitplayDevice,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        manual: true,
        override: (funcs) => {
            const prepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (params) => {
                return {
                    ...prepareGetListParams(params),
                    ...searchParams,
                    service: ORDER_DISPLAY,
                };
            };
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    setFetchedAt(new Date());
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.showDeleteItemConfirm = (id, parent) => {
                const isOrderDisplay = !!parent;
                const deleteFn = isOrderDisplay ? executeDeleteOrder : executeDeleteDevice;
                Modal.confirm({
                    title: translate.formatMessage(
                        isOrderDisplay ? message.deleteOrderDisplayConfirm : message.deleteDeviceConfirm,
                    ),
                    content: '',
                    okText: translate.formatMessage(commonMessage.yes),
                    cancelText: translate.formatMessage(commonMessage.no),
                    centered: true,
                    onOk: async () => {
                        await deleteFn({
                            pathParams: { id },
                            params: !isOrderDisplay ? { service: ORDER_DISPLAY } : {},
                            onCompleted: (response) => {
                                mixinFuncs.onDeleteItemCompleted(id);
                                notification({
                                    message: translate.formatMessage(
                                        isOrderDisplay
                                            ? message.deleteOrderDisplaySuccess
                                            : message.deleteDeviceSuccess,
                                    ),
                                });
                                if (parent) handleFetch(parent.id);
                            },
                            onError: (error) => {
                                mixinFuncs.handleDeleteItemError(error);
                            },
                        });
                    },
                });
            };
            funcs.getItemDetailLink = (dataRow) => {
                const currentPath = location.pathname;
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('collapseId', dataRow.id);
                return `${currentPath}/${dataRow.id}?${searchParams.toString()}`;
            };

            funcs.getCreateLink = () => {
                const currentPath = location.pathname;
                const searchParams = location.search;
                return `${currentPath}/create${searchParams}`;
            };
            funcs.additionalActionColumnButtons = () => {
                const baseButtons = {
                    editMain: (record) => {
                        if (!mixinFuncs.hasPermission([apiConfig.kitplayDevice.update.baseURL])) return null;
                        return (
                            <BaseTooltip title={translate.formatMessage(commonMessage.edit)}>
                                <Button
                                    type="link"
                                    style={{ padding: 0 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingRowId(record.id);
                                        openMainEdit(record);
                                    }}
                                >
                                    <EditOutlined />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                };
                if (!mixinFuncs.hasPermission([apiConfig.kitplayDevice.approveDevice.baseURL])) return baseButtons;
                return {
                    ...baseButtons,
                    loginStatus: (record) => {
                        return (
                            <LoginStatusIndicator
                                lastTimeLogin={record?.lastTimeLogin || record?.device?.lastTimeLogin}
                                fetchedAt={fetchedAt}
                                translate={translate}
                            />
                        );
                    },
                    deleteDevice: ({ id, device }) => (
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={async (e) => {
                                e.stopPropagation();
                                mixinFuncs.showDeleteItemConfirm(id, device.parent);
                            }}
                        >
                            <DeleteOutlined style={{ color: 'red' }} />
                        </Button>
                    ),
                    setting: (record) => {
                        return (
                            <BaseTooltip title={translate.formatMessage(commonMessage.editSetting)}>
                                <Button
                                    type="link"
                                    style={{ padding: 0 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSetting(record);
                                    }}
                                >
                                    <SettingOutlined />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                    editDevice: (record) => {
                        return (
                            <BaseTooltip type="edit">
                                <Button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingChildRowId(record.id);
                                        openChildEdit(record);
                                    }}
                                    type="link"
                                    style={{ padding: 0 }}
                                >
                                    <EditOutlined color="red" />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                };
            };
        },
    });

    const { openMainEdit, openChildEdit, renderModals } = useOrderDeviceFormModal({
        onReloadList: () => mixinFuncs.getList(),
        onReloadChild: (parentId) => handleFetch(parentId),
        onCloseMain: () => setEditingRowId(null),
        onCloseChild: () => setEditingChildRowId(null),
    });

    const expandedRowRender = (record) => {
        const childItems = childData[record.id] || [];
        const isLoading = loadingSubItems[record.id] || false;
        const columns = [
            {
                title: <FormattedMessage defaultMessage="Device name" />,
                dataIndex: ['device', 'name'],
            },
            {
                title: <FormattedMessage defaultMessage="Device ID" />,
                dataIndex: ['device', 'deviceId'],
                width: 120,
            },
            {
                title: <FormattedMessage defaultMessage="Expiration date" />,
                width: 180,
                dataIndex: ['device', 'expireDate'],
                align: 'right',
                render: (expireDate) => {
                    const expireDateLocal = convertUtcToLocalTime(expireDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                    return <div>{expireDateLocal}</div>;
                },
            },
            {
                title: <FormattedMessage defaultMessage="Layout" />,
                width: 120,
                dataIndex: 'layout',
                render: (layout) => {
                    const option = orderDisplayLayouts.find((item) => item.value === layout);
                    return option ? translate.formatMessage(option.label) : null;
                },
            },
            mixinFuncs.renderStatusColumn({
                width: '120px',
                dataIndex: ['device', 'status'],
            }),
            mixinFuncs.renderActionColumn(
                {
                    // setting: { ignorePermission: true },
                    // editDevice: mixinFuncs.hasPermission([apiConfig.orderDisplay.update.baseURL]),
                    // deleteDevice: mixinFuncs.hasPermission([apiConfig.orderDisplay.delete.baseURL]),
                    loginStatus: true,
                },
                { width: '120px', align: 'center' },
            ),
        ];
        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 10,
                        marginBottom: 8,
                        padding: '0 30px',
                    }}
                >
                    <span style={{ fontWeight: 'bold', paddingLeft: 88 }}>
                        {translate.formatMessage(commonMessage.readyBoardMapping)}
                    </span>
                    <SyncOutlined
                        style={{ color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => handleFetch(record.id)}
                    />
                </div>
                <Table
                    className={styles['scroll-container']}
                    columns={columns}
                    dataSource={childItems}
                    pagination={false}
                    rowKey={(record) => record.id}
                    loading={isLoading}
                    rowClassName={(record) =>
                        record.id === editingChildRowId || record.id === editingSettingId
                            ? styles['highlighted-row']
                            : ''
                    }
                />
            </>
        );
    };

    const columns = [
        // {
        //     title: <FormattedMessage defaultMessage="Device name" />,
        //     dataIndex: 'name',
        //     key: 'name',
        //     render: (text, record) => {
        //         return (
        //             <div
        //                 className={styles.div}
        //                 style={{ cursor: 'pointer', color: '#4096ff' }}
        //                 onClick={(event) => {
        //                     event.stopPropagation();
        //                     setData(routes.DeviceTab.keyActiveTab, 'area');
        //                     navigate(
        //                         `${generatePath(routes.listOrderDeviceTabPage.path, {
        //                             customerId: record?.restaurant?.id,
        //                         })}?deviceId=${record.id}&deviceName=${record.name}`,
        //                     );
        //                 }}
        //             >
        //                 {text}
        //             </div>
        //         );
        //     },
        // },
        {
            title: <FormattedMessage defaultMessage="Restaurant name" />,
            dataIndex: ['restaurant', 'restaurantName'],
            render: (text, record) => {
                return (
                    <div
                        className={styles.div}
                        style={{ cursor: 'pointer', color: '#4096ff' }}
                        onClick={(event) => {
                            // event.stopPropagation();
                            // const currentParams = new URLSearchParams(location.search);
                            // setData(routes.DeviceTab.keyActiveTab, 'area');
                            // navigate(
                            //     `${generatePath(routes.listOrderDeviceTabPage.path, {
                            //         customerId: record?.restaurant?.id,
                            //     })}?deviceId=${record.id}&deviceName=${record?.restaurant?.restaurantName}&${currentParams.toString()}`,
                            // );
                        }}
                    >
                        {text}
                    </div>
                );
            },
            // width: 260,
        },
        {
            title: <FormattedMessage defaultMessage="Device ID" />,
            dataIndex: 'deviceId',
            width: 120,
        },
        {
            title: <FormattedMessage defaultMessage="Created date" />,
            width: 180,
            dataIndex: 'createdDate',
            align: 'right',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{createdDateLocal}</div>;
            },
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expireDate',
            align: 'right',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{createdDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                editMain: mixinFuncs.hasPermission([apiConfig.kitplayDevice.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.kitplayDevice.delete.baseURL]),
                loginStatus: true,
            },
            { width: '120px', className: styles['action-column'] },
        ),
    ];

    const searchFields = [
        {
            key: 'restaurantName',
            placeholder: translate.formatMessage(commonMessage.restaurantName),
            colSpan: 4,
        },
        {
            key: 'deviceId',
            placeholder: translate.formatMessage(commonMessage.deviceId),
            colSpan: 4,
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.status),
            type: FieldTypes.SELECT,
            options: statusValues,
            colSpan: 4,
        },
        {
            key: 'expireStatus',
            placeholder: translate.formatMessage(commonMessage.expireDate),
            type: FieldTypes.SELECT,
            options: expireValues,
            colSpan: 6,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.managerOrderDisplay) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({
                    fields: searchFields,
                    initialValues: queryFilter,
                    isReload: true,
                })}
                baseTable={
                    <BaseTable
                        onChange={changePagination}
                        pagination={pagination}
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                        expandable={{
                            expandedRowRender,
                            expandedRowKeys: expandedRowKeys,
                            defaultExpandedRowKeys: ['0'],
                            onExpand: handleExpand,
                        }}
                        rowKey="id"
                        onRow={(record, rowIndex) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                const currentParams = new URLSearchParams(location.search);
                                currentParams.set('collapseId', record.id);
                                setData(routes.listOrderDeviceTabPage.keyActiveTab, 'device');
                                navigate(
                                    `${generatePath(routes.listOrderDeviceTabPage.path, {
                                        customerId: record?.restaurant?.id,
                                    })}?deviceId=${record.id}&deviceName=${record.name
                                    }&customerName=${encodeURIComponent(record?.name)}&${currentParams.toString()}`,
                                );
                            },
                            className: record.id === editingRowId || record.id === editingSettingId ? styles['highlighted-row'] : '',
                        })}
                    />
                }
            />
            {renderModals()}
            <PickUpDisplaySettingModal
                open={openSettingModal}
                onClose={handleCloseSettingModal}
                selectedRecord={selectedSettingRecord}
                restaurantId={
                    Array.isArray(data)
                        ? data.find((item) => item.id === selectedSettingRecord?.device?.parent?.id)?.restaurant?.id
                        : undefined
                }
                onSaveSuccess={() => {
                    mixinFuncs.getList();
                    expandedRowKeys.forEach((id) => {
                        handleFetch(id);
                    });
                }}
            />
        </PageWrapper>
    );
};

export default ListOrderDevicePage;
